import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { ActivatedRouteSnapshot, ActivationEnd, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import UAParser from 'ua-parser-js';
import { AuthService } from '../../modules/auth/services/auth.service';
import { AnalyticsWindow } from './types/analytics-window.type';
import { AdobeDataLayer } from './types/adobe-data-layer.type';
import { AnalyticsEvent, PageLoadStartedEvent, UserDetectedEvent } from './interfaces/analytics-events.interface';
import { AnalyticsEnvironment, DigitalData } from './types/digital-data.type';

@Injectable({
    providedIn: 'root',
})
export class AnalyticsService {
    private isInitialized = false;
    private analyticsEnvironment: AnalyticsEnvironment;
    /** Page loaded/changed events */
    private routerLeafActivationEnd$: Observable<ActivatedRouteSnapshot> = this.router.events.pipe(
        /* get only activation end events */
        filter((event) => {
            return event instanceof ActivationEnd;
        }),
        map((event) => event as ActivationEnd),
        /* get snapshot */
        map((event: ActivationEnd) => {
            return event.snapshot;
        }),
        /* only get leaf route nodes */
        filter((route: ActivatedRouteSnapshot) => {
            return route.firstChild === null;
        }),
    );

    constructor(
        @Inject(LOCALE_ID) private locale: string,
        @Inject(DOCUMENT) private document: Document,
        private router: Router,
        private authService: AuthService,
    ) {}

    private static get windowRef(): AnalyticsWindow {
        return window as AnalyticsWindow;
    }

    private static getOs(): string {
        const userAgentParser = new UAParser();
        const os = userAgentParser.getOS();
        return `${os.name} ${os.version}`;
    }

    public init(config: { script: string; environment: AnalyticsEnvironment }) {
        if (this.isInitialized) {
            return;
        }

        this.isInitialized = true;
        this.analyticsEnvironment = config.environment;

        // declare the adobeDataLayer; the script will later decorate this object
        AnalyticsService.windowRef.adobeDataLayer = AnalyticsService.windowRef.adobeDataLayer || [];
        AnalyticsService.windowRef.dataLayer = AnalyticsService.windowRef.dataLayer || [];

        // load the adobe analytics scripts
        this.addAdobeAnalyticsScript(config.script);

        // page loaded events
        this.routerLeafActivationEnd$.subscribe(async (route) => {
            await this.onRouteChange(route);
        });
    }

    public getAuthEvent() {
        const userId = this.authService.jwtUser?.meta?.userId ?? null;

        return {
            pageInstanceID: this.analyticsEnvironment,
            page: {
                info: {
                    name: 'mdr:login',
                    section: 'mdr',
                    subSection: 'login',
                    subSubSection: '',

                    sysEnv: AnalyticsService.getOs(),
                    serverName: window.location.host,
                    referringURL: decodeURIComponent(localStorage.getItem('reff') || document.referrer),
                    destinationURL: window.location.href,
                    language: this.locale,
                    queryString: window.location.search,
                },
                category: {
                    primary: 'business',
                    secondary: 'mdr',
                },
                attributes: this.getAttributes(),
            },
            user: {
                ID: userId,
                loggedIN: userId !== null ? 'true' : 'false',
            },
        };
    }

    public pushAuthEvents() {
        const digitalData = this.getAuthEvent();

        this.pushEvent({
            event: 'page load started',
            pageInstanceID: digitalData.pageInstanceID,
            page: digitalData.page,
        } as PageLoadStartedEvent);

        this.pushEvent({
            event: 'user detected',
            user: digitalData.user,
        } as UserDetectedEvent);

        this.pushEvent({
            event: 'page loaded',
        });
    }

    private async onRouteChange(route: ActivatedRouteSnapshot) {
        // only send analytics events for routes with config set
        if (!route.routeConfig?.data?.subSection) {
            return;
        }

        const digitalData = await this.getDigitalDataFromRoute(route);
        this.pushEventSet(digitalData);
    }

    private pushEventSet(digitalData: DigitalData) {
        this.pushEvent({
            event: 'page load started',
            pageInstanceID: digitalData.pageInstanceID,
            page: digitalData.page,
        });

        this.pushEvent({
            event: 'user detected',
            user: digitalData.user,
        });

        this.pushEvent({
            event: 'page loaded',
        });
    }

    private pushEvent(event: AnalyticsEvent): void {
        AnalyticsService.windowRef.adobeDataLayer.push((dl: AdobeDataLayer) => {
            dl.push(event);
        });
    }

    private async getDigitalDataFromRoute(route: ActivatedRouteSnapshot): Promise<DigitalData> {
        const subSection = route.routeConfig?.data?.subSection ?? route.routeConfig?.path;
        const subSubSection = route.routeConfig?.data?.subSubSection ?? undefined;

        return this.getDigitalData(subSection, subSubSection);
    }

    private async getDigitalData(subSection: string, subSubSection: string | undefined): Promise<DigitalData> {
        const companyId = this.authService.jwtUser?.CompanyId ?? 'null';
        const section = 'mdr';

        const nameParts = [section, subSection];
        if (subSubSection) {
            nameParts.push(subSubSection);
        }

        const name = nameParts.join(':');

        return {
            pageInstanceID: this.analyticsEnvironment,
            page: {
                info: {
                    name,
                    section,
                    subSection,
                    subSubSection: subSubSection ?? '',

                    sysEnv: AnalyticsService.getOs(),
                    serverName: window.location.host,
                    referringURL: await this.getReferringURL(),
                    destinationURL: window.location.href,
                    language: this.locale,
                    queryString: window.location.search,
                },
                category: {
                    primary: 'business',
                    secondary: 'mdr',
                },
                attributes: this.getAttributes(),
            },
            user: {
                ID: companyId,
                loggedIN: companyId !== null ? 'true' : 'false',
            },
        };
    }

    private addAdobeAnalyticsScript(scriptSrc: string) {
        const scriptElement = this.document.createElement('script');
        scriptElement.src = scriptSrc;
        scriptElement.type = 'text/javascript';
        scriptElement.async = true;

        this.document.head.appendChild(scriptElement);
    }

    private getAttributes() {
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const d = new Date();
        const hour = d.getHours();
        let minutes: string = d.getMinutes().toString();
        if (parseInt(minutes) < 10) {
            minutes = '0' + minutes;
        }

        const day = d.getDate();
        const month = d.getMonth() + 1;
        const year = d.getFullYear();
        let time = hour + ':' + minutes + '|' + hour + ':00-' + hour + ':59' + '|' + days[d.getDay()] + '|gmt ';
        if (d.getTimezoneOffset()) {
            const gmt = (d.getTimezoneOffset() / 60) * -1;
            if (gmt > 0) {
                time += '+' + gmt;
            } else {
                time += gmt;
            }
        }

        let attributeTrackingID: string | undefined = undefined;
        let internalPromotionID: string | undefined = undefined;
        // Split query string (e.g. ?key=value&cid=trackingId&icid=promotionalId)
        const params = new Map<string, string>(
            window.location.search
                .slice(1)
                .split('&')
                .map((keyValue: string): [string, string] => {
                    return keyValue.split('=') as [string, string];
                }),
        );

        if (params.has('cid')) {
            attributeTrackingID = params.get('cid');
        }

        if (params.has('icid')) {
            internalPromotionID = params.get('icid');
        }

        return {
            time,
            date: day + '/' + month + '/' + year,
            domain: 'bitdefender.com',
            domainPeriod: 2,
            trackingID: attributeTrackingID,
            internalPromotionID,
        };
    }

    private async getReferringURL(): Promise<string> {
        return new Promise((resolve, reject) => {
            try {
                AnalyticsService.windowRef.adobeDataLayer.push((dl: AdobeDataLayer) => {
                    try {
                        const previousDigitalData = dl.getState() as DigitalData;
                        return resolve(previousDigitalData?.page?.info?.destinationURL ?? decodeURIComponent(document.referrer));
                    } catch (error) {
                        return reject(error);
                    }
                });
            } catch (error) {
                return reject(error);
            }
        });
    }
}
